/**
 * Tabla **común reutilizable** de órdenes, sobre `components/ui/Table`.
 *
 * La usan las cuatro pantallas de listado y la de búsqueda (`progress/
 * explore_orders_ssr.md`: columnas Orden, Cliente, Tipo, Estado, Estado de pago +
 * acciones). Presentacional: recibe los `items` (vista lista) y dos callbacks
 * (ver detalle); las acciones de transición las pinta `OrderActions`, que ya
 * gatea por permiso. No hace `fetch` ni decide datos.
 */
import { Button, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from "@/components/ui";

import { formatAmount } from "../format";
import type { OrderListItem } from "../types";
import { OrderActions } from "./OrderActions";
import { OrderStatusBadge } from "./OrderStatusBadge";
import { PaymentBadge } from "./PaymentBadge";

interface OrderTableProps {
  items: OrderListItem[];
  /** Abre el detalle de la orden (modal en la pantalla contenedora). */
  onViewDetail: (id: number) => void;
}

export function OrderTable({ items, onViewDetail }: OrderTableProps) {
  if (items.length === 0) {
    return (
      <p className="px-1 py-8 text-center text-sm text-slate-500">
        No hay órdenes para mostrar.
      </p>
    );
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Orden</TableHeaderCell>
          <TableHeaderCell>Cliente</TableHeaderCell>
          <TableHeaderCell>Tipo</TableHeaderCell>
          <TableHeaderCell>Monto</TableHeaderCell>
          <TableHeaderCell>Estado</TableHeaderCell>
          <TableHeaderCell>Pago</TableHeaderCell>
          <TableHeaderCell>
            <span className="sr-only">Acciones</span>
          </TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((order) => (
          <TableRow key={order.id}>
            <TableCell className="font-medium text-slate-900">
              {order.order_number}
            </TableCell>
            <TableCell>{order.customer_name}</TableCell>
            <TableCell className="capitalize">{order.order_type}</TableCell>
            <TableCell>{formatAmount(order.sale_amount)}</TableCell>
            <TableCell>
              <OrderStatusBadge status={order.status} />
            </TableCell>
            <TableCell>
              <PaymentBadge payment={order.payment_status} />
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewDetail(order.id)}
                >
                  Ver detalles
                </Button>
                <OrderActions
                  orderId={order.id}
                  orderNumber={order.order_number}
                  status={order.status}
                  paymentStatus={order.payment_status}
                  size="sm"
                />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
